/**
 * DataWaterfall — one of 5 cockpit loader variants.
 * Ported from landing/components/cockpit/loaders/DataWaterfall.tsx.
 */

import { useEffect, useRef, useState } from 'react';

const DATA_FRAGMENTS = [
  'EURIBOR', '3M', '6M', '12M', '2.614%', '2.537%', '3.108%',
  '475bps', 'EUR', 'Margin', 'Term', '0.750', '1.250', '0.380',
  '2.5M', '5.0M', '10M', 'Tenor', 'Spread', 'Facility',
  'Covenant', 'Benchmark', 'Pricing', 'Collateral', 'Fee',
];

function generateRow(seed: number): string {
  const items: string[] = [];
  for (let i = 0; i < 12; i++) {
    const idx = (seed * 7 + i * 13) % DATA_FRAGMENTS.length;
    items.push(DATA_FRAGMENTS[idx]);
  }
  return items.join('    ');
}

const ROWS = Array.from({ length: 14 }, (_, i) => ({
  text: generateRow(i),
  speed: 15 + (i % 3) * 8,
  direction: i % 2 === 0 ? 1 : -1,
  opacity: 0.08 + (i % 4) * 0.03,
}));

interface Props {
  progress: number;
}

export default function DataWaterfall({ progress }: Props) {
  const [offset, setOffset] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    let lastTime = performance.now();
    const speedMultiplier = 0.5 + (progress / 100) * 2;

    const tick = (now: number) => {
      const delta = (now - lastTime) / 1000;
      lastTime = now;
      setOffset((prev) => prev + delta * 40 * speedMultiplier);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) {cancelAnimationFrame(rafRef.current);}
    };
  }, [progress]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className="absolute inset-0 flex flex-col justify-center gap-1">
        {ROWS.map((row, i) => (
          <div
            key={i}
            className="font-mono text-[12px] whitespace-nowrap"
            style={{
              color: i % 3 === 0 ? 'var(--color-orange)' : 'var(--color-txt-tertiary)',
              opacity: row.opacity + (progress / 100) * 0.06,
              transform: `translateX(${(offset * row.speed * row.direction * 0.02) % 600 - 300}px)`,
            }}
          >
            {row.text}
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="relative z-10 flex flex-col items-center justify-center w-32 h-32 sm:w-40 sm:h-40"
          style={{
            background: 'oklch(from var(--color-surface-main) l c h / 0.92)',
            boxShadow: progress > 5
              ? `0 0 40px oklch(from var(--color-surface-main) l c h / 0.8), 0 0 ${Math.round(progress * 0.15)}px oklch(from var(--color-orange) l c h / ${(progress * 0.002).toFixed(2)})`
              : '0 0 40px oklch(from var(--color-surface-main) l c h / 0.8)',
          }}
        >
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 160 160"
            preserveAspectRatio="none"
          >
            <rect
              x="1" y="1" width="158" height="158"
              fill="none"
              stroke="var(--color-orange)"
              strokeWidth="2"
              strokeDasharray={158 * 2 + 158 * 2}
              strokeDashoffset={(158 * 2 + 158 * 2) * (1 - progress / 100)}
            />
          </svg>
          <div
            className="font-mono text-[48px] font-bold leading-none text-txt-primary tabular-nums"
            style={{ opacity: progress > 3 ? 1 : 0, transition: 'opacity 0.3s ease' }}
          >
            {String(Math.round(progress)).padStart(3, '0')}
          </div>
          <div
            className="font-mono text-[11px] text-txt-tertiary mt-2 tracking-wider uppercase"
            style={{ opacity: progress > 10 ? 1 : 0, transition: 'opacity 0.5s ease' }}
          >
            Credia.pro
          </div>
        </div>
      </div>
    </div>
  );
}
